import { forwardRef, RefAttributes, ReactElement, useMemo, useState, useCallback, FocusEvent, type JSX } from "react";

import { useTheme } from "@mui/material/styles";
import { isEmailMisspelled } from "@simplyk/common";
import { Control, FieldValues, useWatch } from "react-hook-form";

import { useTranslate } from "../../../common/hooks/useTranslate";
import { DashboardTextField } from "../TextField/DashboardTextField";
import { FormTextFieldProps } from "../TextField/types";

import { isEmailAndAllowPlus } from "@/common/helpers/validators";

export interface EmailTextFieldProps<T extends FieldValues> extends FormTextFieldProps<T> {
  checkMisspelledEmail?: boolean;
  variant?: "error" | "warning";
}

export type EmailTextFieldSsrProps<T extends FieldValues> = EmailTextFieldProps<T> & {
  forwardedRef?: React.Ref<HTMLInputElement>;
};

function EmailTextFieldInner<T extends FieldValues>({
  checkMisspelledEmail = true,
  control,
  errorMessage,
  helperText,
  name,
  onBlur,
  onFocus,
  rules,
  variant = "warning",
  forwardedRef,
  ...rest
}: EmailTextFieldSsrProps<T>): ReactElement<FormTextFieldProps<T>> {
  const theme = useTheme();
  const [showMisspellError, setShowMisspellError] = useState(false);
  const { t } = useTranslate();
  const watchedEmailValue = useWatch({ control, name });

  const emailMisspelledError = useMemo(() => {
    if (checkMisspelledEmail) {
      const misspelledResults = isEmailMisspelled(watchedEmailValue as string);
      if (misspelledResults && misspelledResults.length > 0) {
        return t("common", "misspelledEmail");
      }
    }
    return undefined;
  }, [checkMisspelledEmail, t, watchedEmailValue]);

  const currentRules = useMemo(
    () =>
      rules
        ? [...rules, isEmailAndAllowPlus(t("common", "emailFormat"), t("common", "emailFormat.wrongChar"))]
        : [isEmailAndAllowPlus(t("common", "emailFormat"), t("common", "emailFormat.wrongChar"))],
    [rules, t]
  );

  const helperTextColor = useMemo(() => {
    if (emailMisspelledError) {
      return variant === "error" ? theme.palette.error.main : theme.palette.warning.main;
    }
    return undefined;
  }, [emailMisspelledError, variant, theme.palette.error.main, theme.palette.warning.main]);

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onBlur?.(event);
      setShowMisspellError(true);
    },
    [onBlur]
  );

  const handleFocus = useCallback(
    (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onFocus?.(event);
      setShowMisspellError(Boolean(emailMisspelledError));
    },
    [emailMisspelledError, onFocus]
  );

  return (
    <DashboardTextField
      {...rest}
      type="email"
      ref={forwardedRef}
      sx={{
        "& .MuiFormHelperText-root": {
          color: helperTextColor,
        },
      }}
      control={control as unknown as Control<FieldValues, unknown>}
      errorMessage={errorMessage}
      onBlur={handleBlur}
      onFocus={handleFocus}
      helperText={showMisspellError ? emailMisspelledError || helperText : helperText}
      // When variant=error, we want to show the error level on misspelled emails
      error={variant === "error" ? Boolean(emailMisspelledError || rest.error) : rest.error}
      name={name}
      rules={currentRules}
    />
  );
}

export const DashboardEmailTextField = forwardRef(function DashboardEmailTextField<T extends FieldValues>(
  props: EmailTextFieldProps<T>,
  ref: React.Ref<HTMLInputElement>
) {
  return <EmailTextFieldInner {...props} forwardedRef={ref} />;
}) as (<T extends FieldValues>(props: EmailTextFieldProps<T> & RefAttributes<HTMLInputElement>) => JSX.Element) & {
  displayName: string;
};

DashboardEmailTextField.displayName = "DashboardEmailTextField";
